<script lang="ts">
  import { onMount } from 'svelte';
  import type { ApiClient } from '../lib/api';
  import { formatDate } from '../lib/format';
  import GuardDutyFindingList from '../lib/GuardDutyFindingList.svelte';
  import Loading from '../lib/Loading.svelte';
  import StatusBadge from '../lib/StatusBadge.svelte';
  import type { GuardDutyFinding, OverviewCustomer } from '../lib/types';

  let { api }: { api: ApiClient } = $props();
  let customers = $state<OverviewCustomer[]>([]);
  let generatedAt = $state('');
  let findings = $state<GuardDutyFinding[]>([]);
  let loading = $state(true);
  let error = $state('');

  async function load() {
    try {
      const result = await api.overview();
      customers = result.customers;
      findings = result.guardDuty.items;
      generatedAt = result.generatedAt;
      error = '';
    } catch (reason) {
      error = reason instanceof Error ? reason.message : 'Current incidents could not be loaded.';
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, 60_000);
    return () => window.clearInterval(timer);
  });
</script>

<section class="page-heading">
  <div><p class="eyebrow">Current state</p><h1>Operational and security alerts</h1><p>Open CloudWatch alarm lifecycles and recent GuardDuty findings across mapped accounts.</p></div>
  <button class="secondary" onclick={load}>Refresh</button>
</section>
{#if generatedAt}<p class="last-updated">Last successful refresh {formatDate(generatedAt)}</p>{/if}
{#if error}<p class="inline-error" role="alert">{error}{customers.length ? ' Showing the last successful result.' : ''}</p>{/if}
{#if loading}<Loading />
{:else}
  <section class="overview-section"><div class="section-heading"><h2>Customer incidents</h2><span>{customers.reduce((total, group) => total + group.openIncidentCount, 0)}</span></div>
  {#if customers.length === 0}<section class="empty compact"><h3>No open incidents</h3><p>All mapped customer alarms are currently clear.</p></section>
  {:else}
  <div class="customer-stack">
    {#each customers as group}
      <section class="customer-group">
        <header><div><h2>{group.customer.name}</h2><p>{group.openIncidentCount} open {group.openIncidentCount === 1 ? 'incident' : 'incidents'}</p></div><a href={`/customers/${group.customer.id}`}>Customer details</a></header>
        <div class="incident-grid">
          {#each group.incidents as incident}
            <a class="incident-card" href={`/incidents/${incident.id}`}>
              <div><StatusBadge state={incident.latestState} /><time>{formatDate(incident.openedAt)}</time></div>
              <h3>{incident.alarmName}</h3>
              <p>{incident.latestReason}</p>
              <footer><span>{incident.accountName} · {incident.region}</span><span>{incident.eventCount} {incident.eventCount === 1 ? 'event' : 'events'}</span></footer>
            </a>
          {/each}
        </div>
      </section>
    {/each}
  </div>
  {/if}</section>
  <section class="overview-section"><div class="section-heading"><div><h2>Recent GuardDuty findings</h2><p class="muted">Latest 20 medium-or-higher findings from the previous seven days.</p></div><a href="/guardduty">View all findings</a></div>
  {#if findings.length === 0}<section class="empty compact"><h3>No active findings</h3><p>No matching GuardDuty findings were received in this window.</p></section>{:else}<GuardDutyFindingList {findings} />{/if}</section>
{/if}
